import { useState, useEffect } from 'react';
import { declarationsApi } from '../services/api/endpoints/declarations';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type { Declaration } from '../types/declarations';
import type { FiscalYear } from '../types/fiscal-year';

interface UseDeclarationsOptions {
  fiscalYear?: FiscalYear | null;
}

export function useDeclarations(options: UseDeclarationsOptions = {}) {
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { fiscalYear } = options;
  
  // Utiliser les stores pour les filtres globaux
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  
  // Utiliser l'exercice des options ou celui du store
  const activeFiscalYear = fiscalYear || currentFiscalYear;

  useEffect(() => {
    // ⚡ Vider les données de l'ancien exercice avant rechargement
    setDeclarations([]);
    loadDeclarations();
  }, [activeFiscalYear?.id, selectedBusinessUnit?.id]); // Reload when fiscal year OR business unit changes

  const loadDeclarations = async () => {
    try {
      setError(null);
      
      // Load from IndexedDB first
      let localDeclarations = await IndexedDBService.getAll<Declaration>('declarations');
      
      // Protection contre les données corrompues
      localDeclarations = Array.isArray(localDeclarations) ? localDeclarations : [];
      
      // Filtrer par unité commerciale côté client si définie
      if (effectiveFilter.businessUnitId) {
        localDeclarations = localDeclarations.filter(d => 
          (d as any).businessUnitId === effectiveFilter.businessUnitId ||
          effectiveFilter.businessUnitIds?.includes((d as any).businessUnitId) ||
          !(d as any).businessUnitId // Déclarations sans unité sont visibles partout
        );
      }
      
      if (localDeclarations.length > 0) {
        setDeclarations(localDeclarations);
        setLoading(false);
      }

      // If online, fetch from API and update local storage
      if (navigator.onLine) {
        // Prepare filters for API call (fiscalYearId n'est PAS un query param accepté par le backend)
        const filters: any = {};
        if (effectiveFilter.businessUnitId) {
          filters.businessUnitId = effectiveFilter.businessUnitId;
        }
        if (effectiveFilter.businessUnitType) {
          filters.businessUnitType = effectiveFilter.businessUnitType;
        }
        
        const response = await declarationsApi.getAll(filters);
        if (response.success && response.data) {
          // Protection contre les données non-array
          const apiData = Array.isArray(response.data.data) ? response.data.data : [];
          setDeclarations(apiData);
          // Update IndexedDB
          for (const declaration of apiData) {
            await IndexedDBService.update('declarations', declaration);
          }
        }
      }
    } catch (err) {
      console.error('Error loading declarations:', err);
      setError('Impossible de charger les déclarations fiscales');
    } finally {
      setLoading(false);
    }
  };

  const addDeclaration = async (data: Partial<Declaration>): Promise<Declaration> => {
    const newDeclaration: Declaration = {
      id: crypto.randomUUID(),
      type: data.type!,
      period: data.period!,
      periodicity: data.periodicity!,
      dueDate: data.dueDate!,
      status: 'draft',
      amount: data.amount || 0,
      additionalFees: data.additionalFees || 0,
      penalties: data.penalties || 0,
      justificationDocument: data.justificationDocument,
      declarationForm: data.declarationForm,
      notes: data.notes,
      reference: `${data.type}-${data.period}`,
      attachments: []
    };

    // Save to IndexedDB
    await IndexedDBService.add('declarations', newDeclaration);
    setDeclarations(prev => [...prev, newDeclaration]);

    // Add to sync queue if offline
    if (!navigator.onLine) {
      await SyncService.addToSyncQueue({
        id: newDeclaration.id,
        type: 'CREATE',
        entity: 'declarations',
        data: newDeclaration
      });
    } else {
      try {
        const response = await declarationsApi.create(newDeclaration);
        if (response.success && response.data) {
          return response.data;
        }
      } catch (error) {
        console.error('Error creating declaration:', error);
      }
    }

    return newDeclaration;
  };

  const submitDeclaration = async (id: string) => {
    const declaration = declarations.find(d => d.id === id);
    if (!declaration) return;

    try {
      if (navigator.onLine) {
        const response = await declarationsApi.submit(id);
        if (response.success && response.data) {
          setDeclarations(prev => prev.map(d => 
            d.id === id ? { ...d, status: 'submitted' } : d
          ));
          await IndexedDBService.update('declarations', {
            ...declaration,
            status: 'submitted'
          });
        }
      } else {
        await SyncService.addToSyncQueue({
          id: declaration.id,
          type: 'UPDATE',
          entity: 'declarations',
          data: { ...declaration, status: 'submitted' }
        });
      }
    } catch (error) {
      console.error('Error submitting declaration:', error);
      throw error;
    }
  };

  return {
    declarations,
    loading,
    error,
    addDeclaration,
    submitDeclaration,
    loadDeclarations,
    // Exposer les infos de filtre actuels
    currentFilters: {
      fiscalYearId: activeFiscalYear?.id,
      businessUnitId: selectedBusinessUnit?.id,
      businessUnitType: selectedBusinessUnit?.type
    }
  };
}